import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { toPersianDigits } from '../../utils/convertNumbers';
import { CustomAdapterDateFnsJalali, usePersianDatePicker } from '../../hooks/usePersianDatePicker';

const PersianDatePicker = ({ 
    value, 
    onChange, 
    label = "تاریخ", 
    helperText = "",
    format = "yyyy/MM/dd",
    disabled = false,
    readOnly = true,
    error = false,
    required = false,
    ...props 
}) => {
    // Use the custom hook for Persian date picker functionality
    const { convertWeekdays } = usePersianDatePicker();

    // Control the open state so clicking anywhere on the field (not just the
    // calendar icon) opens the picker. The input is readOnly, so a click can't
    // start text entry — it only triggers the calendar.
    const [open, setOpen] = useState(false);

    // Additional effect to ensure weekdays are converted
    useEffect(() => {
        const timer = setTimeout(() => {
            convertWeekdays();
        }, 100);
        
        const timer2 = setTimeout(() => {
            convertWeekdays();
        }, 500);
        
        const timer3 = setTimeout(() => {
            convertWeekdays();
        }, 1000);

        return () => {
            clearTimeout(timer);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [convertWeekdays]);

    // Effect to override placeholder - MUI DatePicker generates it from format
    useEffect(() => {
        const targetPlaceholder = 'روز /  ماه / سال';
        
        const overridePlaceholder = () => {
            // Find all date picker inputs
            const inputs = document.querySelectorAll('input[type="text"]');
            inputs.forEach(input => {
                const isDatePickerInput = input.hasAttribute('readonly') || 
                                         input.closest('.MuiDatePicker-root') || 
                                         input.closest('[class*="DatePicker"]') ||
                                         input.closest('[class*="MuiPickers"]');
                
                if (isDatePickerInput) {
                    const currentPlaceholder = input.getAttribute('placeholder') || input.placeholder || '';
                    // Override if it contains format patterns or is empty
                    if (!currentPlaceholder || 
                        currentPlaceholder.includes('YYYY') || 
                        currentPlaceholder.includes('yyyy') ||
                        currentPlaceholder.includes('MM') ||
                        currentPlaceholder.includes('DD') ||
                        currentPlaceholder.includes('dd') ||
                        currentPlaceholder.match(/\d{4}/)) {
                        input.setAttribute('placeholder', targetPlaceholder);
                        input.placeholder = targetPlaceholder;
                    }
                }
            });
        };

        // Override immediately and on intervals
        overridePlaceholder();
        const intervalId = setInterval(overridePlaceholder, 100);
        
        // Also override on focus events
        const handleFocus = (e) => {
            if (e.target && e.target.tagName === 'INPUT') {
                setTimeout(() => overridePlaceholder(), 10);
            }
        };
        document.addEventListener('focusin', handleFocus, true);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('focusin', handleFocus, true);
        };
    }, [value]);

    return (
        <LocalizationProvider 
            dateAdapter={CustomAdapterDateFnsJalali}
            localeText={{
                calendarWeekNumberHeaderText: 'هفته',
                calendarWeekNumberHeaderLabel: 'هفته',
                calendarWeekNumberAriaLabelText: week => `هفته ${week}`,
                calendarWeekNumberText: week => `${week}`,
            }}
        >
            <DatePicker
                label={label}
                value={value}
                onChange={onChange}
                format={format}
                disabled={disabled}
                open={open}
                // Merge slotProps: our textField config takes priority, then merge any props.slotProps
                slotProps={{
                    // Don't override calendarHeader navigation behavior. The popper gets a
                    // class so a plain-CSS rule (see index.css) can mirror the prev/next
                    // arrow glyphs (without moving them) so each points the other way.
                    // Weekday conversion is handled via onOpen/onClose/onViewChange
                    ...(props.slotProps || {}),
                    popper: {
                        ...(props.slotProps?.popper || {}),
                        className: ['persian-date-picker-popper', props.slotProps?.popper?.className]
                            .filter(Boolean)
                            .join(' '),
                    },
                    textField: {
                        variant: 'outlined',
                        fullWidth: true,
                        size: 'small',
                        helperText: helperText,
                        error: error,
                        required: required,
                        // Open the picker when the field itself is clicked, not just the icon.
                        onClick: () => { if (!disabled) setOpen(true); },
                        sx: {
                            textAlign: 'right',
                            '& .MuiInputBase-root, & input': { cursor: 'pointer' },
                        },
                        placeholder: "روز /  ماه / سال",
                        inputProps: {
                            readOnly: readOnly,
                        },
                        InputProps: {
                            readOnly: readOnly,
                            // Custom input component to display Persian digits
                            inputComponent: React.forwardRef(function PersianInputComponent(inputProps, ref) {
                                const { value, ...other } = inputProps;
                                const persianValue = value ? toPersianDigits(value) : '';
                                return (
                                    <input
                                        {...other}
                                        ref={ref}
                                        value={persianValue}
                                        readOnly={readOnly}
                                    />
                                );
                            }),
                        },
                        ...(props.slotProps?.textField || {}),
                    },
                }}
                // Use onOpen to trigger weekday conversion when calendar opens
                onOpen={() => {
                    setOpen(true);
                    setTimeout(() => convertWeekdays(), 100);
                    setTimeout(() => convertWeekdays(), 300);
                }}
                // Use onClose to trigger conversion after month changes when calendar closes
                onClose={() => {
                    setOpen(false);
                    setTimeout(() => convertWeekdays(), 100);
                }}
                // Watch for view changes (month/year navigation) to trigger weekday conversion
                onViewChange={(view) => {
                    if (view === 'day') {
                        // When navigating months, the view stays as 'day' but content changes
                        setTimeout(() => convertWeekdays(), 100);
                        setTimeout(() => convertWeekdays(), 300);
                    }
                }}
                // Extract slotProps from props to avoid overriding our config
                {...(() => {
                    const { slotProps: _, ...restProps } = props;
                    return restProps;
                })()}
            />
        </LocalizationProvider>
    );
};

export default PersianDatePicker;